import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useWatchList } from "../lib/useWatchList";
import { getEventDetail, getResults } from "../lib/api";
import type {
  RaceDayEventDetail,
  RaceDayResultRow,
  RaceDayResults,
} from "../lib/types";
import { isRowWatched } from "../lib/watchlist";

interface Hit {
  event: RaceDayEventDetail;
  category: { id: string; name: string };
  stage: { id: string; name: string };
  row: RaceDayResultRow;
  status: string;
}

const SCAN_CONCURRENCY = 4;
const RESULTS_TTL_MS = 60_000;
const resultsCache = new Map<string, { ts: number; value: RaceDayResults }>();

async function cachedResults(stageId: string, categoryId: string) {
  const key = `${stageId}:${categoryId}`;
  const hit = resultsCache.get(key);
  if (hit && Date.now() - hit.ts < RESULTS_TTL_MS) return hit.value;
  const { results } = await getResults({ stageId, categoryId });
  resultsCache.set(key, { ts: Date.now(), value: results });
  return results;
}

async function runWithLimit<T>(
  limit: number,
  tasks: Array<() => Promise<T>>,
): Promise<void> {
  let i = 0;
  const workers = Array.from({ length: Math.min(limit, tasks.length) }, async () => {
    while (i < tasks.length) {
      const idx = i++;
      try {
        await tasks[idx]();
      } catch {
        /* one bad task shouldn't kill the scan */
      }
    }
  });
  await Promise.all(workers);
}

export default function Home() {
  const watch = useWatchList();
  const [hits, setHits] = useState<Hit[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (watch.events.length === 0 || watch.riders.length === 0) {
      setHits([]);
      return;
    }
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const found: Hit[] = [];
        for (const eventId of watch.events) {
          const { event } = await getEventDetail(eventId);
          if (cancelled) return;
          const tasks: Array<() => Promise<void>> = [];
          for (const stage of event.stages) {
            for (const category of event.categories) {
              tasks.push(async () => {
                const results = await cachedResults(stage.id, category.id);
                if (cancelled) return;
                for (const row of results.rows) {
                  if (isRowWatched(row.name, watch.riders)) {
                    found.push({
                      event,
                      category,
                      stage,
                      row,
                      status: results.status,
                    });
                  }
                }
              });
            }
          }
          await runWithLimit(SCAN_CONCURRENCY, tasks);
        }
        if (!cancelled) setHits(found);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // We re-run when the watch list changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch.events.join(","), watch.riders.join(",")]);

  if (watch.events.length === 0 || watch.riders.length === 0) {
    return <FirstRun watch={watch} />;
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-mono font-bold mb-1">Your watch list</h1>
        <p className="text-sm text-pit-muted font-mono">
          {watch.riders.length} rider{watch.riders.length === 1 ? "" : "s"} ·{" "}
          {watch.events.length} event{watch.events.length === 1 ? "" : "s"}
        </p>
      </div>

      {loading && <div className="text-sm text-pit-muted font-mono">Scanning…</div>}
      {error && <div className="text-sm text-pit-accent font-mono">{error}</div>}

      {!loading && hits.length === 0 && !error && (
        <div className="rounded-md border border-pit-border pit-panel p-6 text-sm text-pit-muted font-mono">
          No matches yet across your watched events.
        </div>
      )}

      <div className="space-y-3">
        {hits.map((hit, i) => (
          <Link
            key={i}
            to={`/event/${hit.event.id}/c/${hit.category.id}?rider=${encodeURIComponent(hit.row.name.split(",")[0])}`}
            className="block rounded-md border border-pit-border pit-panel p-4 hover:border-pit-accent transition-colors"
          >
            <div className="flex items-baseline justify-between gap-3">
              <div className="font-mono font-bold">{hit.row.name}</div>
              <div className="font-mono text-pit-gold tabular-nums">
                {hit.row.place ? `P${hit.row.place}` : ""}
              </div>
            </div>
            <div className="text-xs text-pit-muted font-mono mt-1">
              {hit.event.name} · {hit.event.date} · {hit.category.name}
            </div>
            <div className="text-xs text-pit-muted font-mono mt-1 flex gap-3">
              {hit.row.totalTime && <span>time {hit.row.totalTime}</span>}
              {hit.row.gap && <span>gap {hit.row.gap}</span>}
              <span className="ml-auto">{hit.status}</span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function FirstRun({ watch }: { watch: { riders: string[]; events: string[] } }) {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-mono font-bold mb-2">RaceDay permalink</h1>
        <p className="text-sm text-pit-muted leading-relaxed">
          A bookmarkable, shareable view over results.racedayeventsoftware.com. Pin the riders and events you care about and they'll surface here.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <Link to="/events" className="rounded-md border border-pit-border pit-panel p-5 hover:border-pit-accent transition-colors">
          <div className="font-mono font-bold mb-1">Browse events</div>
          <div className="text-xs text-pit-muted">
            Pick an event and category. The URL bar will actually mean something.
          </div>
        </Link>
        <Link to="/watch" className="rounded-md border border-pit-border pit-panel p-5 hover:border-pit-accent transition-colors">
          <div className="font-mono font-bold mb-1">Set up watch list</div>
          <div className="text-xs text-pit-muted">
            Add riders ({watch.riders.length} so far) and events ({watch.events.length}) to track.
          </div>
        </Link>
      </div>
    </div>
  );
}
